import React, { useState, useEffect } from 'react';
import { Bell, Clock, Trophy, Settings, Check, AlertCircle, TestTube, Info, CheckCircle, XCircle } from 'lucide-react';
import { NotificationPreferences as NotificationPreferencesType } from '../../types/notifications';
import { getUserNotificationPreferences, updateNotificationPreferences, sendTestNotification } from '../../lib/notificationStore';
import { Toast } from '../ui/Toast';
import { isIOSDevice, isIOSWebViewGold, isWebViewGold } from '../../lib/platform';
import { auth, db } from '../../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';

interface NotificationPreferencesProps {
  userId: string;
}

interface NotificationGroup {
  id: string;
  title: string;
  icon: React.ElementType;
  items: {
    key: keyof NotificationPreferencesType;
    label: string;
    description: string;
  }[];
}

const NOTIFICATION_GROUPS: NotificationGroup[] = [
  {
    id: 'baking',
    title: 'Baking Sessions',
    icon: Clock,
    items: [
      {
        key: 'sessionReminders',
        label: 'Session Reminders',
        description: 'Get notified about kneading, shaping, and baking times'
      }
    ]
  },
  {
    id: 'starters',
    title: 'Starter Management',
    icon: Bell,
    items: [
      {
        key: 'starterFeeding',
        label: 'Feeding Reminders',
        description: 'Receive alerts when your starters need feeding'
      }
    ]
  },
  {
    id: 'challenges',
    title: 'Challenges & Updates',
    icon: Trophy,
    items: [
      {
        key: 'challengeUpdates',
        label: 'Challenge Notifications',
        description: 'Get notified about new challenges and updates'
      }
    ]
  },
  {
    id: 'system',
    title: 'System Notifications',
    icon: Settings,
    items: [
      {
        key: 'systemNotifications',
        label: 'System Updates',
        description: 'Important updates and announcements'
      },
      {
        key: 'pushNotifications',
        label: 'Push Notifications',
        description: 'Receive notifications on your device'
      },
      {
        key: 'soundEnabled',
        label: 'Notification Sounds',
        description: 'Play a sound when notifications arrive'
      }
    ]
  }
];

export function NotificationPreferences({ userId }: NotificationPreferencesProps) {
  const [preferences, setPreferences] = useState<NotificationPreferencesType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [tokenPlatform, setTokenPlatform] = useState<string | null>(null);
  const [loadingToken, setLoadingToken] = useState(false);

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await getUserNotificationPreferences(userId);
        setPreferences(prefs);
      } catch (err) {
        setError('Failed to load notification preferences');
        console.error('Error loading preferences:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [userId]);

  const handleToggle = (key: keyof NotificationPreferencesType) => {
    if (!preferences) return;

    setPreferences(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [key]: !prev[key]
      };
    });
  };

  const handleSelectAll = (value: boolean) => {
    if (!preferences) return;

    const updatedPrefs: Partial<NotificationPreferencesType> = {};
    NOTIFICATION_GROUPS.forEach(group => {
      group.items.forEach(item => {
        updatedPrefs[item.key] = value;
      });
    });

    setPreferences(prev => ({
      ...prev!,
      ...updatedPrefs
    }));
  };

  const handleSave = async () => {
    if (!preferences) return;

    setSaving(true);
    setError(null);

    try {
      await updateNotificationPreferences(preferences);
      setToastMessage('Notification preferences saved successfully');
      setShowToast(true);
    } catch (err) {
      setError('Failed to save notification preferences');
      console.error('Error saving preferences:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async () => {
    setTesting(true);
    setError(null);

    try {
      console.log('Sending test notification...');
      await sendTestNotification();
      setToastMessage('Test notification sent! Check the notification bell.');
      setShowToast(true);
      console.log('✓ Test notification created successfully');
    } catch (err: any) {
      console.error('❌ Failed to send test notification:', err);
      setError(err.message || 'Failed to send test notification');
    } finally {
      setTesting(false);
    }
  };

  const checkFCMToken = async () => {
    setLoadingToken(true);
    try {
      const tokenDoc = doc(db, 'users', userId, 'notificationTokens', 'current');
      const tokenSnapshot = await getDoc(tokenDoc);

      if (tokenSnapshot.exists()) {
        const data = tokenSnapshot.data();
        setFcmToken(data.token || null);
        setTokenPlatform(data.platform || null);
      } else {
        setFcmToken(null);
        setTokenPlatform(null);
      }
    } catch (err) {
      console.error('Error checking FCM token:', err);
      setFcmToken(null);
      setTokenPlatform(null);
    } finally {
      setLoadingToken(false);
    }
  };

  useEffect(() => {
    if (showDiagnostics) {
      checkFCMToken();
    }
  }, [showDiagnostics, userId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="w-6 h-6 border-2 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!preferences) return null;

  const allEnabled = NOTIFICATION_GROUPS.every(group =>
    group.items.every(item => preferences[item.key])
  );

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-3 bg-red-500/10 backdrop-blur-sm rounded-lg border border-red-500/20">
          <p className="text-sm text-red-200">{error}</p>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-white">Notification Preferences</h3>
        <button
          onClick={() => handleSelectAll(!allEnabled)}
          className="text-sm text-amber-400 hover:text-amber-300 transition-colors"
        >
          {allEnabled ? 'Disable All' : 'Enable All'}
        </button>
      </div>

      <div className="space-y-6">
        {NOTIFICATION_GROUPS.map(group => (
          <div key={group.id} className="space-y-4">
            <div className="flex items-center gap-2 text-white/90">
              <group.icon className="w-5 h-5" />
              <h4 className="font-medium">{group.title}</h4>
            </div>

            <div className="space-y-3">
              {group.items.map(item => (
                <label
                  key={item.key}
                  className="flex items-start gap-3 p-3 bg-white/10 rounded-xl backdrop-blur-sm cursor-pointer hover:bg-white/[0.15] transition-colors"
                >
                  <div className="flex-shrink-0 pt-0.5">
                    <div
                      className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                        preferences[item.key]
                          ? 'bg-amber-500 border-amber-500'
                          : 'border-white/30 bg-white/10'
                      }`}
                      onClick={() => handleToggle(item.key)}
                    >
                      {preferences[item.key] && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-white">{item.label}</div>
                    <p className="text-sm text-white/70 mt-0.5">
                      {item.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-white/10 pt-6">
        <button
          onClick={() => setShowDiagnostics(!showDiagnostics)}
          className="flex items-center gap-2 text-sm text-white/70 hover:text-white mb-4 transition-colors"
        >
          <Info className="w-4 h-4" />
          {showDiagnostics ? 'Hide' : 'Show'} Notification Diagnostics
        </button>

        {showDiagnostics && (
          <div className="mb-6 p-4 bg-white/5 border border-white/10 rounded-xl space-y-3">
            <h4 className="text-sm font-medium text-white mb-2">Push Notification Status</h4>

            <div className="flex items-start gap-2 text-sm">
              {isIOSDevice() ? (
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
              ) : (
                <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <span className="text-white/90">Device Type: </span>
                <span className="text-white/70">
                  {isIOSDevice() ? 'iOS' : 'Android/Desktop'}
                </span>
              </div>
            </div>

            <div className="flex items-start gap-2 text-sm">
              {isIOSWebViewGold() ? (
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
              ) : isIOSDevice() ? (
                <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              ) : (
                <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <span className="text-white/90">WebViewGold: </span>
                <span className="text-white/70">
                  {isWebViewGold() ? 'Detected' : 'Not detected'}
                  {isIOSDevice() && !isIOSWebViewGold() && ' (Required for iOS notifications)'}
                </span>
              </div>
            </div>

            <div className="flex items-start gap-2 text-sm">
              {loadingToken ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin flex-shrink-0 mt-0.5" />
              ) : fcmToken ? (
                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <span className="text-white/90">FCM Token: </span>
                <span className="text-white/70">
                  {loadingToken ? 'Checking...' : fcmToken ? 'Registered' : 'Not registered'}
                </span>
                {fcmToken && (
                  <div className="mt-1 text-xs text-white/50 font-mono break-all">
                    {fcmToken.substring(0, 20)}...{fcmToken.substring(fcmToken.length - 20)}
                  </div>
                )}
                {tokenPlatform && (
                  <div className="mt-1 text-xs text-white/50">
                    Platform: {tokenPlatform}
                  </div>
                )}
              </div>
            </div>

            {!fcmToken && !loadingToken && (
              <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded text-xs text-amber-200">
                No FCM token found. The app needs to register for push notifications first.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-between items-center">
        <button
          onClick={handleTestNotification}
          disabled={testing}
          className={`px-6 py-2 rounded-xl transition-all duration-300 flex items-center gap-2 ${
            testing
              ? 'bg-white/10 text-white/50 cursor-not-allowed'
              : 'bg-blue-500/90 hover:bg-blue-500 text-white'
          }`}
        >
          {testing ? (
            <>
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
              Testing...
            </>
          ) : (
            <>
              <TestTube className="w-4 h-4" />
              Test Notification
            </>
          )}
        </button>

        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-6 py-2 rounded-xl transition-all duration-300 ${
            saving
              ? 'bg-white/10 text-white/50 cursor-not-allowed'
              : 'bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-500 hover:to-amber-600 text-white'
          }`}
        >
          {saving ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </div>
          ) : (
            'Save Preferences'
          )}
        </button>
      </div>

      {showToast && (
        <Toast
          message={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
}